import React, { useState, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export function DhabaDetailScreen({ dhaba, onBack, onOrder }) {
  const [activeTab, setActiveTab] = useState('menu');
  const [selectedImage, setSelectedImage] = useState(0);
  const [dhabaPhotos, setDhabaPhotos] = useState(null);
  const [showBadgeViewer, setShowBadgeViewer] = useState(false);
  const [menuData, setMenuData] = useState(null);
  const [menuLoading, setMenuLoading] = useState(false);
  const [cart, setCart] = useState({});

  const addToCart = (item) => {
    setCart((prev) => ({
      ...prev,
      [item.id]: { ...item, quantity: (prev[item.id]?.quantity || 0) + 1 },
    }));
  };
  const decFromCart = (itemId) => {
    setCart((prev) => {
      const next = { ...prev };
      if (next[itemId]?.quantity > 1) {
        next[itemId] = { ...next[itemId], quantity: next[itemId].quantity - 1 };
      } else {
        delete next[itemId];
      }
      return next;
    });
  };
  const cartItems = Object.values(cart);
  const cartTotalItems = cartItems.reduce((s, i) => s + i.quantity, 0);
  const cartTotalAmount = cartItems.reduce((s, i) => s + i.price * i.quantity, 0);

  // Fetch full dhaba detail (with photos) on mount
  useEffect(() => {
    if (!dhaba?.id) return;
    fetch(`${API_BASE}/api/v1/dhabas/${dhaba.id}`)
      .then(r => r.json())
      .then(data => {
        if (data.success && data.dhaba?.photos) {
          setDhabaPhotos(data.dhaba.photos);
        }
      })
      .catch(() => {});
  }, [dhaba?.id]);

  // Fetch menu when Menu tab is opened
  useEffect(() => {
    if (activeTab !== 'menu' || !dhaba?.id || menuData) return;
    setMenuLoading(true);
    fetch(`${API_BASE}/api/v1/menu/dhaba/${dhaba.id}`)
      .then(r => r.json())
      .then(data => {
        if (data.success && data.menu) {
          setMenuData(data.menu);
        }
      })
      .catch(() => {})
      .finally(() => setMenuLoading(false));
  }, [activeTab, dhaba?.id, menuData]);

  // Normalize nested API data
  const amenities = dhaba?.amenities || {};
  const timings = dhaba?.timings || {};

  // Build images from categorized photos first, then legacy flat images
  const photos = dhabaPhotos || dhaba?.photos || {};
  const PHOTO_ORDER = ['exterior', 'dining', 'kitchen', 'food_storage', 'washroom', 'parking', 'menu_board'];
  const allPhotos = PHOTO_ORDER.flatMap(cat => (photos[cat] || []).map(url => ({ url, category: cat })));
  const legacyImages = (dhaba?.images || []).map(img => ({ url: typeof img === 'string' ? img : img.url, category: 'legacy' }));
  const singleImage = dhaba?.image ? [{ url: dhaba.image, category: 'exterior' }] : [];
  const allImages = allPhotos.length > 0 ? allPhotos : legacyImages.length > 0 ? legacyImages : singleImage;
  const images = allImages.length > 0 ? allImages : [
    { url: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=600&h=400&fit=crop', category: 'placeholder' },
  ];

  const amenityIcons = {
    parking: { icon: '🅿️', label: 'Parking' },
    washroom: { icon: '🚻', label: 'Washroom' },
    ac_dining: { icon: '❄️', label: 'AC Dining' },
    wifi: { icon: '📶', label: 'Free WiFi' },
    card_accepted: { icon: '💳', label: 'Cards Accepted' },
    family_friendly: { icon: '👨‍👩‍👧‍👦', label: 'Family Friendly' },
    play_area: { icon: '🎠', label: 'Kids Play Area' },
    ev_charging: { icon: '⚡', label: 'EV Charging' },
  };

  const getAmenities = () => {
    const result = [];
    Object.keys(amenityIcons).forEach(key => {
      if (amenities[key]) {
        result.push(amenityIcons[key]);
      }
    });
    return result;
  };

  const isOpen = dhaba?.is_open !== false;
  const isOpen24x7 = timings.open_24x7;
  const isPureVeg = dhaba?.tags?.includes('Pure Veg');
  const contactPhone = dhaba?.phone || dhaba?.contact_phone;
  const DHABA_TYPE_LABELS = { dhaba: 'Dhaba', restaurant: 'Restaurant', food_court: 'Food Court' };
  const rawType = dhaba?.dhaba_type || dhaba?.type;
  const dhabaType = DHABA_TYPE_LABELS[rawType] || (rawType ? rawType.charAt(0).toUpperCase() + rawType.slice(1) : 'Highway Dhaba');

  return (
    <div style={{ minHeight: '100vh', background: '#FFFFFF', paddingBottom: '100px' }}>
      {/* Header with Image */}
      <div style={{ position: 'relative' }}>
        {/* Main Image */}
        <div style={{ height: '280px', position: 'relative', overflow: 'hidden' }}>
          <img 
            src={images[selectedImage]?.url} 
            alt={dhaba?.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          {/* Gradient overlay */}
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '120px',
            background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)'
          }} />
          
          {/* Back Button */}
          <button
            onClick={onBack}
            style={{
              position: 'absolute',
              top: '16px',
              left: '16px',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.95)',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }}
            data-testid="back-btn"
          >
            ←
          </button>
          
          {/* Share & Favorite */}
          <div style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            display: 'flex',
            gap: '10px'
          }}>
            <button data-testid="favorite-btn" style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.95)',
              border: 'none',
              cursor: 'pointer',
              fontSize: '18px'
            }}>
              ♡
            </button>
            <button data-testid="share-btn" style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.95)',
              border: 'none',
              cursor: 'pointer',
              fontSize: '16px'
            }}>
              ↗
            </button>
          </div>
          
          {/* Image indicators */}
          {images.length > 1 && (
            <div style={{
              position: 'absolute',
              bottom: '80px',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: '6px'
            }}>
              {images.map((_, idx) => (
                <div
                  key={`indicator-${idx}`}
                  onClick={() => setSelectedImage(idx)}
                  style={{
                    width: idx === selectedImage ? '20px' : '8px',
                    height: '8px',
                    borderRadius: '4px',
                    background: idx === selectedImage ? '#D4AF37' : 'rgba(255,255,255,0.6)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                />
              ))}
            </div>
          )}
          
          {/* Score Badge — based on certification */}
          {(() => {
            const isCertDhaba = dhaba?.ht_certified_dhaba;
            const isCertDining = dhaba?.ht_certified_dining && !isCertDhaba;
            const isCertWashroom = dhaba?.ht_certified_washroom && !isCertDhaba && !isCertDining;
            let score = null;
            let bg = '';
            let color = '';
            if (isCertDhaba || isCertDining) {
              score = parseFloat(dhaba?.hygiene_score || 0).toFixed(1);
              bg = isCertDhaba ? 'linear-gradient(135deg, #D4AF37, #C5A028)' : '#22C55E';
              color = isCertDhaba ? '#1A1A1A' : '#FFFFFF';
            } else if (isCertWashroom) {
              score = parseFloat(dhaba?.washroom_rating || 0).toFixed(1);
              bg = '#3B82F6';
              color = '#FFFFFF';
            }
            if (!score || score === '0.0') return null;
            return (
              <div style={{
                position: 'absolute', bottom: '20px', right: '16px',
                background: bg, padding: '6px 12px', borderRadius: '8px',
                display: 'flex', alignItems: 'center', gap: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
              }}>
                <span style={{ color, fontWeight: '700', fontSize: '15px' }}>⭐ {score}</span>
              </div>
            );
          })()}
        </div>
        
        {/* Thumbnail strip */}
        {images.length > 1 && (
          <div style={{
            display: 'flex',
            gap: '8px',
            padding: '12px 16px',
            overflowX: 'auto',
            background: '#FAFAFA'
          }}>
            {images.map((img, idx) => (
              <img
                key={`thumb-${idx}`}
                src={img.url}
                alt=""
                onClick={() => setSelectedImage(idx)}
                style={{
                  width: '60px',
                  height: '45px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  border: idx === selectedImage ? '2px solid #D4AF37' : '2px solid transparent',
                  cursor: 'pointer',
                  opacity: idx === selectedImage ? 1 : 0.7
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Dhaba Info */}
      <div style={{ padding: '20px 16px' }}>
        {/* Name & Type */}
        <div style={{ marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <h1 data-testid="dhaba-name" style={{ 
              fontSize: '24px', 
              fontWeight: '700', 
              color: '#1A1A1A',
              fontFamily: "'Manrope', sans-serif",
            }}>
              {dhaba?.name || 'Dhaba'}
            </h1>
          </div>
          <p style={{ color: '#6B7280', fontSize: '14px' }}>
            {dhabaType} • {dhaba?.highway || ''}
          </p>
        </div>

        {/* Option A: HT Certification Trust Banner */}
        {(() => {
          const isCertDhaba = dhaba?.ht_certified_dhaba;
          const isCertDining = dhaba?.ht_certified_dining && !isCertDhaba;
          const isCertWashroom = dhaba?.ht_certified_washroom && !isCertDhaba && !isCertDining;
          if (!isCertDhaba && !isCertDining && !isCertWashroom) return null;

          let bg, borderColor, titleColor, subtitleColor, badgeSrc, title, scores;
          if (isCertDhaba) {
            bg = 'linear-gradient(135deg, rgba(212,175,55,0.12), rgba(212,175,55,0.04))';
            borderColor = '#D4AF37';
            titleColor = '#92700C';
            subtitleColor = '#A8860D';
            badgeSrc = '/images/badges/ht-certified-dhaba.png';
            title = 'HT Certified Dhaba';
            scores = `Dining ${parseFloat(dhaba.hygiene_score || 0).toFixed(1)}/5  •  Washroom ${parseFloat(dhaba.washroom_rating || 0).toFixed(1)}/5`;
          } else if (isCertDining) {
            bg = 'linear-gradient(135deg, rgba(34,197,94,0.1), rgba(34,197,94,0.03))';
            borderColor = '#22C55E';
            titleColor = '#065F46';
            subtitleColor = '#047857';
            badgeSrc = '/images/badges/ht-certified-dining.png';
            title = 'HT Certified Dining';
            scores = `Hygiene Score ${parseFloat(dhaba.hygiene_score || 0).toFixed(1)}/5`;
          } else {
            bg = 'linear-gradient(135deg, rgba(59,130,246,0.1), rgba(59,130,246,0.03))';
            borderColor = '#3B82F6';
            titleColor = '#1E40AF';
            subtitleColor = '#1D4ED8';
            badgeSrc = '/images/badges/ht-certified-washroom.png';
            title = 'HT Certified Washroom';
            scores = `Washroom Score ${parseFloat(dhaba.washroom_rating || 0).toFixed(1)}/5`;
          }

          return (
            <div style={{
              background: bg, border: `1.5px solid ${borderColor}`, borderRadius: '14px',
              padding: '14px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '14px'
            }}>
              <img src={badgeSrc} alt={title} onClick={() => setShowBadgeViewer(true)}
                style={{ height: '50px', objectFit: 'contain', flexShrink: 0, cursor: 'pointer' }} />
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: '15px', fontWeight: '700', color: titleColor, marginBottom: '2px', fontFamily: "'Manrope', sans-serif" }}>
                  {title}
                </p>
                <p style={{ fontSize: '13px', fontWeight: '600', color: subtitleColor, marginBottom: '3px' }}>
                  {scores}
                </p>
                <p style={{ fontSize: '11px', color: '#6B7280' }}>
                  Verified for hygiene & cleanliness by HiWayTrip inspection team
                </p>
              </div>
            </div>
          );
        })()}

        {/* Tags */}
        {dhaba?.tags?.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
            {dhaba.tags.map(tag => (
              <span
                key={tag}
                style={{
                  padding: '6px 12px',
                  borderRadius: '20px',
                  background: 'linear-gradient(135deg, #1A1A1A 0%, #2D2D2D 50%, #D4AF37 100%)',
                  color: '#FFFFFF',
                  fontSize: '12px',
                  fontWeight: '500'
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}
        
        {/* Quick Info Cards */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(3, 1fr)', 
          gap: '10px',
          marginBottom: '20px'
        }}>
          {/* Timing */}
          <div style={{
            padding: '14px 10px',
            background: '#F9FAFB',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <span style={{ fontSize: '20px', display: 'block', marginBottom: '6px' }}>🕐</span>
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#1A1A1A', display: 'block' }}>
              {isOpen24x7 ? 'Open 24×7' : `${timings.open_time || '6:00'} - ${timings.close_time || '23:00'}`}
            </span>
            <span style={{ 
              fontSize: '11px', 
              color: isOpen ? '#22C55E' : '#EF4444',
              fontWeight: '500'
            }}>
              {isOpen ? 'Open Now' : 'Closed'}
            </span>
          </div>
          
          {/* Price */}
          <div style={{
            padding: '14px 10px',
            background: '#F9FAFB',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <span style={{ fontSize: '20px', display: 'block', marginBottom: '6px' }}>💰</span>
            <span style={{ fontSize: '14px', fontWeight: '700', color: '#1A1A1A', display: 'block' }}>
              {dhaba?.price_range || '₹₹'}
            </span>
            <span style={{ fontSize: '11px', color: '#6B7280' }}>
              {dhaba?.price_range === '₹' ? 'Budget' : dhaba?.price_range === '₹₹₹' ? 'Premium' : 'Moderate'}
            </span>
          </div>
          
          {/* Distance */}
          <div style={{
            padding: '14px 10px',
            background: '#F9FAFB',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <span style={{ fontSize: '20px', display: 'block', marginBottom: '6px' }}>📍</span>
            <span style={{ fontSize: '14px', fontWeight: '700', color: '#1A1A1A', display: 'block' }}>
              {dhaba?.distance || '—'}
            </span>
            <span style={{ fontSize: '11px', color: '#6B7280' }}>
              ~{dhaba?.time || dhaba?.travel_time || '—'}
            </span>
          </div>
        </div>
        
        {/* Address */}
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          padding: '14px',
          background: '#F9FAFB',
          borderRadius: '12px',
          marginBottom: '20px'
        }}>
          <span style={{ fontSize: '20px' }}>📍</span>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: '14px', color: '#1A1A1A', lineHeight: '1.5' }}>
              {dhaba?.address || 'Address not available'}
            </p>
            <button 
              data-testid="get-directions-btn"
              style={{
                marginTop: '8px',
                padding: '8px 16px',
                background: '#1A1A1A',
                color: '#D4AF37',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Get Directions →
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '20px',
          borderBottom: '1px solid #E5E7EB',
          paddingBottom: '12px'
        }}>
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'menu', label: 'Menu' },
            { id: 'reviews', label: 'Reviews' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              style={{
                padding: '10px 20px',
                borderRadius: '25px',
                border: 'none',
                background: activeTab === tab.id ? '#1A1A1A' : '#F3F4F6',
                color: activeTab === tab.id ? '#D4AF37' : '#6B7280',
                fontWeight: '600',
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div>
            {/* Option C: HT Certification Detail Section */}
            {(dhaba?.ht_certified_dhaba || dhaba?.ht_certified_dining || dhaba?.ht_certified_washroom) && (() => {
              const isCertDhaba = dhaba?.ht_certified_dhaba;
              const isCertDining = dhaba?.ht_certified_dining;
              const isCertWashroom = dhaba?.ht_certified_washroom;
              const hygieneVal = parseFloat(dhaba?.hygiene_score || 0).toFixed(1);
              const washroomVal = parseFloat(dhaba?.washroom_rating || 0).toFixed(1);

              return (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1A1A1A', marginBottom: '12px' }}>
                    HT Certification
                  </h3>
                  <div style={{ background: '#F9FAFB', borderRadius: '14px', padding: '16px' }}>
                    {/* Badge + Title */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                      <img src={isCertDhaba ? '/images/badges/ht-certified-dhaba.png' : isCertDining ? '/images/badges/ht-certified-dining.png' : '/images/badges/ht-certified-washroom.png'}
                        alt="" style={{ height: '60px', objectFit: 'contain' }} />
                      <div>
                        <p style={{ fontSize: '16px', fontWeight: '700', color: '#1A1A1A', fontFamily: "'Manrope', sans-serif" }}>
                          {isCertDhaba ? 'HT Certified Dhaba' : isCertDining ? 'HT Certified Dining' : 'HT Certified Washroom'}
                        </p>
                        <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
                          Independently audited by HiWayTrip
                        </p>
                      </div>
                    </div>

                    {/* Score bars */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
                      {(isCertDhaba || isCertDining) && (
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Dining Hygiene</span>
                            <span style={{ fontSize: '13px', fontWeight: '700', color: parseFloat(hygieneVal) >= 4 ? '#22C55E' : '#F59E0B' }}>{hygieneVal}/5</span>
                          </div>
                          <div style={{ height: '8px', background: '#E5E7EB', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${(parseFloat(hygieneVal) / 5) * 100}%`, background: parseFloat(hygieneVal) >= 4 ? '#22C55E' : '#F59E0B', borderRadius: '4px' }} />
                          </div>
                        </div>
                      )}
                      {(isCertDhaba || isCertWashroom) && (
                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>Washroom Quality</span>
                            <span style={{ fontSize: '13px', fontWeight: '700', color: parseFloat(washroomVal) >= 4 ? '#22C55E' : '#F59E0B' }}>{washroomVal}/5</span>
                          </div>
                          <div style={{ height: '8px', background: '#E5E7EB', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${(parseFloat(washroomVal) / 5) * 100}%`, background: parseFloat(washroomVal) >= 4 ? '#22C55E' : '#F59E0B', borderRadius: '4px' }} />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* What this means */}
                    <div style={{ padding: '12px', background: '#FFFFFF', borderRadius: '10px', border: '1px solid #E5E7EB' }}>
                      <p style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                        What does HT Certified mean?
                      </p>
                      <ul style={{ fontSize: '12px', color: '#6B7280', lineHeight: '1.8', paddingLeft: '16px', margin: 0 }}>
                        <li>Physically inspected by HiWayTrip audit team</li>
                        <li>Scored across {isCertDhaba ? '47' : isCertDining ? '23' : '24'} hygiene & safety parameters</li>
                        <li>Minimum 4/5 score required for certification</li>
                        <li>Re-audited periodically — certification can be revoked</li>
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Cuisine Types */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1A1A1A', marginBottom: '12px' }}>
                Cuisine
              </h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {(dhaba?.cuisine || ['North Indian']).map(cuisine => (
                  <span
                    key={cuisine}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '20px',
                      border: '2px solid #D4AF37',
                      background: 'rgba(212, 175, 55, 0.1)',
                      color: '#92700C',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}
                  >
                    {cuisine}
                  </span>
                ))}
              </div>
            </div>
            
            {/* Amenities */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1A1A1A', marginBottom: '12px' }}>
                Amenities
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                {(getAmenities().length > 0 ? getAmenities() : [
                  { icon: '🅿️', label: 'Parking' },
                  { icon: '🚻', label: 'Washroom' },
                  { icon: '📶', label: 'WiFi' },
                  { icon: '💳', label: 'Cards' },
                ]).map((amenity, idx) => (
                  <div
                    key={amenity.label}
                    style={{
                      padding: '12px 8px',
                      background: '#F9FAFB',
                      borderRadius: '12px',
                      textAlign: 'center'
                    }}
                  >
                    <span style={{ fontSize: '24px', display: 'block', marginBottom: '6px' }}>{amenity.icon}</span>
                    <span style={{ fontSize: '11px', color: '#4B5563', fontWeight: '500' }}>{amenity.label}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Parking Info */}
            {amenities.parking && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1A1A1A', marginBottom: '12px' }}>
                  Parking Available
                </h3>
                <div style={{ display: 'flex', gap: '10px' }}>
                  {(amenities.parking_type
                    ? (Array.isArray(amenities.parking_type) ? amenities.parking_type : [amenities.parking_type])
                    : ['car', 'truck']
                  ).map(type => (
                    <span
                      key={type}
                      style={{
                        padding: '10px 16px',
                        borderRadius: '10px',
                        background: '#F3F4F6',
                        fontSize: '13px',
                        fontWeight: '500',
                        color: '#374151'
                      }}
                    >
                      {type === 'car' && '🚗 Car'}
                      {type === 'truck' && '🚛 Truck'}
                      {type === 'bus' && '🚌 Bus'}
                      {type === 'both' && '🅿️ Car & Truck'}
                      {type === 'all' && '🅿️ All Vehicles'}
                      {!['car', 'truck', 'bus', 'both', 'all'].includes(type) && `🅿️ ${type}`}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Contact */}
            {contactPhone && (
              <div style={{
                padding: '16px',
                background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, rgba(212, 175, 55, 0.05) 100%)',
                borderRadius: '14px',
                border: '1px solid rgba(212, 175, 55, 0.3)'
              }}>
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#92700C', marginBottom: '12px' }}>
                  Contact
                </h3>
                <a 
                  href={`tel:${contactPhone}`}
                  data-testid="call-now-btn"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: '12px',
                    background: '#1A1A1A',
                    color: '#D4AF37',
                    borderRadius: '10px',
                    textDecoration: 'none',
                    fontWeight: '600',
                    fontSize: '15px'
                  }}
                >
                  Call Now: {contactPhone}
                </a>
              </div>
            )}
          </div>
        )}

        {activeTab === 'menu' && (
          <div>
            {menuLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{ width: '32px', height: '32px', border: '3px solid #E5E7EB', borderTopColor: '#D4AF37', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            ) : menuData && menuData.total_items > 0 ? (
              <div>
                {menuData.categories.map(cat => (
                  <div key={cat.name} style={{ marginBottom: '20px' }}>
                    <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#1A1A1A', marginBottom: '12px', textTransform: 'capitalize' }}>
                      {cat.name.replace('_', ' ')} ({cat.items.length})
                    </h4>
                    {cat.items.map(item => {
                      const qty = cart[item.id]?.quantity || 0;
                      return (
                      <div key={item.id} style={{ display: 'flex', gap: '12px', padding: '14px 0', borderBottom: '1px solid #F3F4F6' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                            <span style={{ width: '14px', height: '14px', border: `2px solid ${item.is_veg ? '#22C55E' : '#EF4444'}`, borderRadius: '3px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: item.is_veg ? '#22C55E' : '#EF4444' }} />
                            </span>
                            {item.is_bestseller && <span style={{ fontSize: '11px', color: '#D4AF37', fontWeight: '600' }}>Bestseller</span>}
                          </div>
                          <p style={{ fontSize: '15px', fontWeight: '600', color: '#1A1A1A', marginBottom: '2px' }}>{item.name}</p>
                          <p style={{ fontSize: '15px', fontWeight: '700', color: '#1A1A1A' }}>₹{item.price}</p>
                          {item.description && <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>{item.description}</p>}
                          <p style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '2px' }}>Prep: ~{item.prep_time_mins || 15} min</p>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                          {item.image ? (
                            <img src={item.image} alt={item.name} style={{ width: '90px', height: '70px', objectFit: 'cover', borderRadius: '10px' }} />
                          ) : (
                            <div style={{ width: '90px', height: '70px', background: '#F3F4F6', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>🍽️</div>
                          )}
                          {qty === 0 ? (
                            <button
                              onClick={() => addToCart(item)}
                              data-testid={`menu-add-${item.id}`}
                              style={{
                                padding: '6px 20px',
                                borderRadius: '8px',
                                border: '1.5px solid #D4AF37',
                                background: '#FFF',
                                color: '#B8860B',
                                fontWeight: '700',
                                fontSize: '13px',
                                cursor: 'pointer',
                                letterSpacing: '0.5px',
                                width: '90px',
                              }}
                            >
                              ADD
                            </button>
                          ) : (
                            <div
                              data-testid={`menu-qty-${item.id}`}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                border: '1.5px solid #D4AF37',
                                borderRadius: '8px',
                                background: '#FFFBEB',
                                width: '90px',
                                padding: '2px 4px',
                              }}
                            >
                              <button
                                onClick={() => decFromCart(item.id)}
                                style={{ width: '24px', height: '28px', border: 'none', background: 'transparent', color: '#B8860B', fontWeight: '700', fontSize: '16px', cursor: 'pointer' }}
                              >−</button>
                              <span style={{ fontSize: '14px', fontWeight: '700', color: '#B8860B' }}>{qty}</span>
                              <button
                                onClick={() => addToCart(item)}
                                style={{ width: '24px', height: '28px', border: 'none', background: 'transparent', color: '#B8860B', fontWeight: '700', fontSize: '16px', cursor: 'pointer' }}
                              >+</button>
                            </div>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>📋</span>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1A1A1A', marginBottom: '8px' }}>Menu Not Available</h3>
                <p style={{ color: '#6B7280', fontSize: '14px' }}>This dhaba hasn't added their menu yet</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'reviews' && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>⭐</span>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1A1A1A', marginBottom: '8px' }}>
              Reviews Coming Soon
            </h3>
            <p style={{ color: '#6B7280', fontSize: '14px' }}>
              Be the first to review this dhaba
            </p>
          </div>
        )}
      </div>

      {/* Badge Viewer Modal */}
      {showBadgeViewer && (() => {
        const isCertDhaba = dhaba?.ht_certified_dhaba;
        const isCertDining = dhaba?.ht_certified_dining;
        const isCertWashroom = dhaba?.ht_certified_washroom;
        const badgeSrc = isCertDhaba ? '/images/badges/ht-certified-dhaba.png' : isCertDining ? '/images/badges/ht-certified-dining.png' : '/images/badges/ht-certified-washroom.png';
        const title = isCertDhaba ? 'HT Certified Dhaba' : isCertDining ? 'HT Certified Dining' : 'HT Certified Washroom';
        return (
          <div onClick={() => setShowBadgeViewer(false)} style={{
            position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.85)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px',
          }}>
            <button onClick={() => setShowBadgeViewer(false)} style={{
              position: 'absolute', top: '16px', right: '16px', width: '40px', height: '40px',
              borderRadius: '50%', background: 'rgba(255,255,255,0.2)', border: 'none', color: '#FFF', fontSize: '20px', cursor: 'pointer',
            }}>✕</button>
            <img src={badgeSrc} alt={title} onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: '280px', maxHeight: '280px', objectFit: 'contain', marginBottom: '24px' }} />
            <p style={{ color: '#D4AF37', fontSize: '20px', fontWeight: '700', fontFamily: "'Manrope', sans-serif", marginBottom: '8px' }}>{title}</p>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', textAlign: 'center', maxWidth: '300px' }}>
              This dhaba has been physically inspected and certified by the HiWayTrip audit team for hygiene and cleanliness standards.
            </p>
          </div>
        );
      })()}

      {/* Bottom CTA */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        padding: '16px',
        background: '#FFFFFF',
        borderTop: '1px solid #E5E7EB',
        display: 'flex',
        gap: '12px',
        maxWidth: '28rem',
        margin: '0 auto'
      }}>
        <button
          onClick={() => onOrder && onOrder(dhaba, cartItems.length > 0 ? cart : null)}
          style={{
            flex: 1,
            padding: '16px',
            borderRadius: '14px',
            border: 'none',
            background: 'linear-gradient(135deg, #D4AF37 0%, #C5A028 100%)',
            color: '#1A1A1A',
            fontWeight: '700',
            fontSize: '16px',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(212, 175, 55, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
          }}
          data-testid="order-now-btn"
        >
          {cartItems.length > 0 ? (
            <>
              <span>{cartTotalItems} item{cartTotalItems !== 1 ? 's' : ''} • ₹{cartTotalAmount}</span>
              <span>Review Order →</span>
            </>
          ) : (
            <span style={{ flex: 1, textAlign: 'center' }}>Order Now</span>
          )}
        </button>
        <button
          data-testid="book-table-btn"
          style={{
            padding: '16px 20px',
            borderRadius: '14px',
            border: '2px solid #1A1A1A',
            background: 'transparent',
            color: '#1A1A1A',
            fontWeight: '600',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          Book Table
        </button>
      </div>
    </div>
  );
}

export default DhabaDetailScreen;
